import React, { useMemo, useState } from 'react';
import { VStack, Button, StackDivider, Flex } from '@chakra-ui/react';
import { EMerchantPixelsAndKeys } from '@olo-web/types/enums/merchantPixelsAndKeys.enum';
import { EditableInputWithButtons } from '@olo-web/components/molecules/EditableInputWithButtons';
import { useMerchantTheme } from '@domain/merchants/queries/useMerchantTheme';
import { useUpdateMerchantTheme } from '@domain/merchants/mutations/useUpdateMerchantTheme';
import { useToast } from '@olo-web/utils/common/hooks';

const labels = {
  [EMerchantPixelsAndKeys.FACEBOOK_PIXEL]: 'Facebook Pixel Id',
  [EMerchantPixelsAndKeys.GOOGLE_TAG_MANAGER]: 'Google Tag Manager Key',
  [EMerchantPixelsAndKeys.GOOGLE_ADWORDS_KEY]: 'Google Adwords Key',
  [EMerchantPixelsAndKeys.GOOGLE_ADWORDS_CONVERSION_LABEL]: 'Google Adwords Conversion Label',
};

const placeholders = {
  [EMerchantPixelsAndKeys.FACEBOOK_PIXEL]: 'ie, 394826374528379',
  [EMerchantPixelsAndKeys.GOOGLE_TAG_MANAGER]: 'ie, GTM-87HK738',
  [EMerchantPixelsAndKeys.GOOGLE_ADWORDS_KEY]: 'ie, AW-728374639',
  [EMerchantPixelsAndKeys.GOOGLE_ADWORDS_CONVERSION_LABEL]: 'ie, VdKLPNhu8fwDWMi_m84A',
};

export const MerchantPixelsAndKeys = () => {
  const [data, setData] = useState({});
  const { notify } = useToast();
  const { mutate, isLoading } = useUpdateMerchantTheme({
    onSuccess: () => {
      notify({
        status: 'success',
        title: 'Pixels and keys saved',
        description: `Your changes are now live`,
        position: 'bottom-left',
        isClosable: true,
        variant: 'left-accent',
      });
      setData({});
    },
    onError: (error) => {
      notify({
        status: 'error',
        title: `Error updating pixels and keys`,
        description: error?.message || 'Please contact support',
        position: 'bottom-left',
        isClosable: true,
        variant: 'left-accent',
      });
    },
  });
  const { data: theme } = useMerchantTheme();

  const keys = useMemo(() => {
    return Object.keys(EMerchantPixelsAndKeys).map((k) => EMerchantPixelsAndKeys[k]);
  }, []);

  const handleChange = (key: string, value: string) => {
    if (value.includes('None added') || value === theme?.[key]) return;
    setData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    mutate({
      theme: data,
    });
  };

  return (
    <Flex flexDir="column" w="100%">
      <VStack
        spacing={4}
        divider={<StackDivider />}
        align="flex-start"
        justify="flex-start"
        w="100%"
      >
        {keys.map((key) => (
          <EditableInputWithButtons
            key={key}
            onSubmit={(value: string) => handleChange(key, value)}
            defaultValue={theme![key] || `None added (${placeholders[key]})`}
            label={labels[key]}
          />
        ))}
      </VStack>
      {!!Object.keys(data).length && (
        <Button
          alignSelf="flex-end"
          colorScheme="primary"
          variant="solid"
          onClick={handleSave}
          isLoading={isLoading}
          mt={8}
        >
          Save
        </Button>
      )}
    </Flex>
  );
};
